import React, { useState, useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { makeStyles } from '@material-ui/core';
import { useSidebarOpenState } from '@backstage/core-components';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import Collapse from '@material-ui/core/Collapse';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import ArrowRightIcon from '@material-ui/icons/ArrowRight';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import CategoryOutlined from '@material-ui/icons/CategoryOutlined';
import GavelOutlined from '@material-ui/icons/GavelOutlined';
import DeviceHubOutlined from '@material-ui/icons/DeviceHubOutlined';
import AssessmentOutlined from '@material-ui/icons/AssessmentOutlined';
import MemoryOutlined from '@material-ui/icons/MemoryOutlined';
import DashboardOutlined from '@material-ui/icons/DashboardOutlined';
import DeveloperBoardOutlined from '@material-ui/icons/DeveloperBoardOutlined';
import { DFP_NAVIGATION, DfpNavIconKey, DfpNavItem } from '@internal/plugin-dfp-common';

// Fallback palette values used when the Backstage navigation palette is absent
const FALLBACK_NAV_COLOR      = 'rgba(208, 208, 208, 0.82)';
const FALLBACK_SELECTED_COLOR = '#ffffff';
const FALLBACK_INDICATOR      = '#7df3e1';
const FALLBACK_HOVER_BG       = 'rgba(255, 255, 255, 0.08)';

interface NavItem {
  id: string;
  label: string;
  path?: string;
  icon?: React.ReactElement;
  children?: NavItem[];
  usePrefix?: boolean; // leaf items that should be active on any sub-path
}

const NAV_ICONS: Record<DfpNavIconKey, React.ReactElement> = {
  overview: <InfoOutlinedIcon fontSize="small" />,
  productMaster: <CategoryOutlined fontSize="small" />,
  standards: <GavelOutlined fontSize="small" />,
  platforms: <DeviceHubOutlined fontSize="small" />,
  healthHub: <AssessmentOutlined fontSize="small" />,
  infra: <MemoryOutlined fontSize="small" />,
  dashboards: <DashboardOutlined fontSize="small" />,
};

function toNavItem(item: DfpNavItem): NavItem {
  return {
    id: item.id,
    label: item.label,
    path: item.route,
    icon: item.iconKey ? NAV_ICONS[item.iconKey] : undefined,
    usePrefix: item.usePrefix,
    children: item.children?.map(toNavItem),
  };
}

const NAV_TREE: NavItem[] = DFP_NAVIGATION.map(toNavItem);

function isPathActive(itemPath: string, currentPath: string): boolean {
  if (itemPath === '/dfp') {
    return currentPath === '/dfp' || currentPath === '/dfp/';
  }
  if (currentPath === itemPath) return true;
  if (currentPath.startsWith(`${itemPath}/`)) return true;
  return false;
}

function isUnderDfp(currentPath: string): boolean {
  return currentPath === '/dfp' || currentPath.startsWith('/dfp/');
}

function findExpandedParents(items: NavItem[], currentPath: string, parents: string[] = []): string[] {
  const result: string[] = [];
  for (const item of items) {
    if (item.path && isPathActive(item.path, currentPath)) {
      result.push(...parents);
    }
    if (item.children) {
      result.push(...findExpandedParents(item.children, currentPath, [...parents, item.id]));
    }
  }
  return result;
}

const useStyles = makeStyles((theme) => {
  // Use Backstage's navigation-specific palette tokens so colours automatically
  // match built-in SidebarItems across every theme — including Backstage's light
  // theme, which intentionally keeps the sidebar dark (navigation.background:
  // '#171717'). Relying on palette.type === 'dark' would return false in light
  // mode and cause invisible black-on-dark text.
  const nav = (theme.palette as Record<string, any>).navigation ?? {};
  const textColor: string      = nav.color                    ?? FALLBACK_NAV_COLOR;
  const selectedColor: string  = nav.selectedColor            ?? FALLBACK_SELECTED_COLOR;
  const indicatorColor: string = nav.indicator                ?? FALLBACK_INDICATOR;
  const hoverBg: string        = nav.navItem?.hoverBackground ?? FALLBACK_HOVER_BG;
  // Active background and scrollbar thumb depend on whether the sidebar is
  // light or dark. With our custom themes, palette.type reliably tells us:
  //   'light' → light sidebar (#f5f5f5)  →  dark overlay + dark scrollbar
  //   'dark'  → dark  sidebar (#161616)  →  light overlay + light scrollbar
  const isLightSidebar         = theme.palette.type === 'light';
  const activeBg               = isLightSidebar ? 'rgba(0, 0, 0, 0.12)' : 'rgba(255, 255, 255, 0.12)';
  const scrollThumb            = isLightSidebar ? 'rgba(0, 0, 0, 0.18)'  : 'rgba(255, 255, 255, 0.12)';

  // ── Shared token set — kept identical to sidebarStyles.css overrides ──
  const itemBase = {
    display: 'flex',
    alignItems: 'center',
    height: 48,                               // matches SidebarItem
    cursor: 'pointer',
    color: textColor,
    borderLeft: '3px solid transparent',      // always reserve border space
    borderRadius: 0,
    transition: 'background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease',
    userSelect: 'none' as const,
    '&:hover': {
      backgroundColor: hoverBg,
      color: selectedColor,
    },
  };

  const itemActiveBase = {
    backgroundColor: activeBg,
    color: selectedColor,
    borderLeft: `3px solid ${indicatorColor}`,
  };

  // ── Parent row icon (full size — 22 px matches built-in SidebarItem) ──
  const iconBase = {
    minWidth: 'auto',
    marginRight: 12,
    color: 'inherit',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& svg': { fontSize: 20 },
  };

  // ── Parent row text ─────────────────────────────────────────────────
  // fontWeight matches Backstage's SidebarItem label class (fontWeight: "bold")
  // which overrides the Typography subtitle2 default of 500.
  const textBase = {
    flex: '1 1 auto',
    minWidth: 0,
    margin: 0,
    '& .MuiListItemText-primary': {
      fontSize: 14,
      fontWeight: 700,
      letterSpacing: '0.01em',
      color: 'inherit',
      whiteSpace: 'nowrap' as const,
      overflow: 'hidden' as const,
      textOverflow: 'ellipsis' as const,
      lineHeight: 'auto',
    },
  };

  // ── Child item icon (compact — 18 px to save horizontal space) ──────
  const childIconBase = {
    minWidth: 'auto',
    marginRight: 8,
    color: 'inherit',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& svg': { fontSize: 18 },
  };

  // ── Child item text (13 px keeps long labels within 240 px sidebar) ─
  const childTextBase = {
    flex: '1 1 auto',
    minWidth: 0,
    margin: 0,
    '& .MuiListItemText-primary': {
      fontSize: 13,
      fontWeight: 500,
      letterSpacing: 0,
      color: 'inherit',
      whiteSpace: 'nowrap' as const,
      overflow: 'hidden' as const,
      textOverflow: 'ellipsis' as const,
      lineHeight: 1.4,
    },
  };

  const textActiveBase = {
    '& .MuiListItemText-primary': {
      fontWeight: 700,
      color: 'inherit',
    },
  };

  return {
    // ── Parent "Dev » Portal" row ──────────────────────────────────────
    parentItem: {
      ...itemBase,
      // left: 23px aligns icon centre (3px border + 23px pad + 10px half-icon = 36px)
      // which matches Backstage SidebarItem whose icon is centred in a 72px container.
      // right: 10px mirrors the `right: 10` used by root SidebarItem expand arrows.
      padding: '0 10px 0 23px',
    },
    parentItemActive: {
      ...itemActiveBase,
    },
    parentIcon: { ...iconBase },
    parentText: { ...textBase },
    expandIcon: {
      fontSize: 24,
      color: 'inherit',
      opacity: 0.6,
      flexShrink: 0,
      marginLeft: 4,
      transition: 'opacity 0.15s ease',
    },
    expandIconOpen: { opacity: 0.85 },

    // ── Children container ─────────────────────────────────────────────
    childrenContainer: {
      overflowY: 'auto',
      overflowX: 'hidden',
      maxHeight: 420,
      '&::-webkit-scrollbar': { width: 3 },
      '&::-webkit-scrollbar-track': { background: 'transparent' },
      '&::-webkit-scrollbar-thumb': {
        background: scrollThumb,
        borderRadius: 2,
      },
    },

    // ── Child nav items ────────────────────────────────────────────────
    navItem: {
      ...itemBase,
      height: 44,              // slightly more compact than parent (48px)
      padding: '0 10px 0 0',  // 10px right mirrors root SidebarItem; left from nesting class
    },
    navItemActive: { ...itemActiveBase },
    navItemIcon: { ...childIconBase },
    navItemText: { ...childTextBase },
    navItemTextActive: { ...textActiveBase },
    childExpandIcon: {
      fontSize: 22,
      color: 'inherit',
      opacity: 0.6,
      flexShrink: 0,
      marginLeft: 4,
      transition: 'opacity 0.15s ease',
    },
    childExpandIconOpen: { opacity: 0.85 },

    // ── Nesting offsets tuned for 240 px sidebar ───────────────────────
    nested1: { paddingLeft: 23 },   // aligns with parent icon column
    nested2: { paddingLeft: 35 },   // +12px per level
    nested3: { paddingLeft: 47 },

    // ── Collapsed (sidebar pinned off / icon-only) ─────────────────────
    // Mirrors how Backstage's SidebarItem renders in closed state: a single
    // icon centred in the full closed-drawer width.
    closedItem: {
      ...itemBase,
      padding: 0,
      justifyContent: 'center',
    },
    closedItemIcon: {
      fontSize: 20,
      color: 'inherit',
    },
  };
});

// ── ChildNavItem ───────────────────────────────────────────────────────────

interface ChildNavItemProps {
  item: NavItem;
  depth: number;
  expanded: Set<string>;
  onToggle: (id: string) => void;
  onNavigate: (path: string) => void;
  currentPath: string;
}

const ChildNavItem: React.FC<ChildNavItemProps> = ({
  item,
  depth,
  expanded,
  onToggle,
  onNavigate,
  currentPath,
}) => {
  const classes = useStyles();
  const isLeaf = !item.children || item.children.length === 0;
  const isExpanded = expanded.has(item.id);

  // Determine if this nav item is active
  let isActive = false;
  if (item.path) {
    if (!isLeaf || item.usePrefix) {
      isActive = isPathActive(item.path, currentPath);
    } else {
      isActive = currentPath === item.path;
    }
  }

  // Determine nesting class based on depth
  let nestingClass = classes.nested3;
  if (depth === 1) {
    nestingClass = classes.nested1;
  } else if (depth === 2) {
    nestingClass = classes.nested2;
  }

  const handleClick = () => {
    if (isLeaf && item.path) {
      onNavigate(item.path);
    } else {
      onToggle(item.id);
      if (item.path) onNavigate(item.path);
    }
  };

  return (
    <>
      <ListItem
        className={[
          classes.navItem,
          isActive ? classes.navItemActive : '',
          nestingClass,
        ].join(' ')}
        onClick={handleClick}
        dense
        disableGutters
      >
        {item.icon && (
          <ListItemIcon className={classes.navItemIcon}>{item.icon}</ListItemIcon>
        )}
        <ListItemText
          primary={item.label}
          className={[
            classes.navItemText,
            isActive ? classes.navItemTextActive : '',
          ].join(' ')}
        />
        {!isLeaf &&
          (isExpanded ? (
            <ArrowDropDownIcon className={`${classes.childExpandIcon} ${classes.childExpandIconOpen}`} />
          ) : (
            <ArrowRightIcon className={classes.childExpandIcon} />
          ))}
      </ListItem>

      {!isLeaf && (
        <Collapse in={isExpanded} timeout={180} unmountOnExit>
          <List component="div" disablePadding>
            {item.children!.map(child => (
              <ChildNavItem
                key={child.id}
                item={child}
                depth={depth + 1}
                expanded={expanded}
                onToggle={onToggle}
                onNavigate={onNavigate}
                currentPath={currentPath}
              />
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
};

// ── DfpSidebarNav ──────────────────────────────────────────────────────────

export const DfpSidebarNav: React.FC = () => {
  const classes = useStyles();
  const location = useLocation();
  const navigate = useNavigate();
  const { isOpen } = useSidebarOpenState();
  const [dfpExpanded, setDfpExpanded] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (isUnderDfp(location.pathname)) {
      setDfpExpanded(true);
      const parentIds = findExpandedParents(NAV_TREE, location.pathname);
      if (parentIds.length > 0) {
        setExpanded(prev => {
          const next = new Set(prev);
          parentIds.forEach(id => next.add(id));
          return next;
        });
      }
    }
  }, [location.pathname]);

  const handleToggle = useCallback((id: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const handleNavigate = useCallback(
    (path: string) => navigate(path),
    [navigate],
  );

  const handleDfpClick = () => {
    setDfpExpanded(prev => !prev);
    if (!dfpExpanded) navigate('/dfp');
  };

  const isDfpActive = isUnderDfp(location.pathname);

  // Collapsed (sidebar not pinned open) — show only the icon, matching the
  // behaviour of every other SidebarItem in icon-only mode.
  if (!isOpen) {
    return (
      <ListItem
        button
        className={[
          classes.closedItem,
          isDfpActive ? classes.parentItemActive : '',
        ].join(' ')}
        onClick={() => navigate('/dfp')}
        disableGutters
      >
        <DeveloperBoardOutlined className={classes.closedItemIcon} />
      </ListItem>
    );
  }

  return (
    <>
      <ListItem
        className={[
          classes.parentItem,
          isDfpActive ? classes.parentItemActive : '',
        ].join(' ')}
        onClick={handleDfpClick}
        disableGutters
      >
        <ListItemIcon className={classes.parentIcon}>
          <DeveloperBoardOutlined />
        </ListItemIcon>
        <ListItemText primary="Dev » Portal" className={classes.parentText} />
        {dfpExpanded ? (
          <ArrowDropDownIcon className={`${classes.expandIcon} ${classes.expandIconOpen}`} />
        ) : (
          <ArrowRightIcon className={classes.expandIcon} />
        )}
      </ListItem>

      <Collapse in={dfpExpanded} timeout={200} unmountOnExit>
        <div className={classes.childrenContainer}>
          <List component="div" disablePadding>
            {NAV_TREE.map(item => (
              <ChildNavItem
                key={item.id}
                item={item}
                depth={1}
                expanded={expanded}
                onToggle={handleToggle}
                onNavigate={handleNavigate}
                currentPath={location.pathname}
              />
            ))}
          </List>
        </div>
      </Collapse>
    </>
  );
};
